/**
 * Coverage Matrix domain grouping — classifies checklist requirements
 * into user-facing domains for scannable matrix layout.
 */
import type { ChecklistItem } from '../../config/documentChecklists';
import type { SedSectionId } from '../../types/siteEvidence';

export type CoverageRequirementDomain =
  | 'site'
  | 'grid'
  | 'environmental'
  | 'water'
  | 'connectivity'
  | 'other';

export const COVERAGE_DOMAIN_ORDER: readonly CoverageRequirementDomain[] = [
  'site',
  'connectivity',
  'environmental',
  'water',
  'grid',
  'other',
];

export const COVERAGE_DOMAIN_LABELS: Record<CoverageRequirementDomain, string> = {
  site: 'Site',
  grid: 'Grid',
  environmental: 'Environmental',
  water: 'Water',
  connectivity: 'Connectivity',
  other: 'Other',
};

const SECTION_TO_DOMAIN: Partial<Record<SedSectionId, CoverageRequirementDomain>> = {
  seismic: 'site',
  geotechnical: 'site',
  parcel: 'site',
  siteProfile: 'site',
  flood: 'site',
  regulatory: 'site',
  topography: 'site',
  survey: 'site',
  mineralResources: 'site',
  grid: 'grid',
  electricity: 'grid',
  environmental: 'environmental',
  culturalResources: 'environmental',
  siteResearch: 'environmental',
  waterStress: 'water',
  waterResource: 'water',
  connectivity: 'connectivity',
};

/** Explicit overrides where sedSectionsHelpful spans domains or is ambiguous. */
export const REQUIREMENT_DOMAIN_OVERRIDES: Partial<Record<string, CoverageRequirementDomain>> = {
  'site-project-intent': 'site',
  geotech: 'site',
  'discharge-permit': 'water',
  'municipal-utility-letter': 'water',
  'community-feedback-summary': 'environmental',
  'diligence-source-pack': 'other',
  'prior-report-pack': 'other',
  'vendor-quote-pack': 'other',
  'btm-vendor-quote': 'grid',
};

const TAG_DOMAIN_KEYWORDS: ReadonlyArray<{ domain: CoverageRequirementDomain; patterns: string[] }> = [
  { domain: 'grid', patterns: ['utility', 'interconnect', 'grid', 'iso', 'rto', 'tariff', 'ppa', 'queue'] },
  { domain: 'environmental', patterns: ['wetland', 'species', 'esa', 'phase', 'cultural', 'shpo', 'community', 'stakeholder'] },
  { domain: 'water', patterns: ['water', 'aquifer', 'groundwater', 'npdes', 'discharge'] },
  { domain: 'connectivity', patterns: ['fiber', 'latency', 'carrier', 'peering'] },
  { domain: 'site', patterns: ['geotech', 'parcel', 'zoning', 'flood', 'survey', 'topo', 'title', 'mineral'] },
];

function domainFromTags(patterns: readonly string[]): CoverageRequirementDomain | undefined {
  const haystack = patterns.join(' ').toLowerCase();
  for (const { domain, patterns: keywords } of TAG_DOMAIN_KEYWORDS) {
    if (keywords.some(k => haystack.includes(k))) return domain;
  }
  return undefined;
}

/**
 * Classify one checklist requirement into a coverage matrix domain.
 */
export function classifyRequirementDomain(item: ChecklistItem): CoverageRequirementDomain {
  const override = REQUIREMENT_DOMAIN_OVERRIDES[item.id];
  if (override) return override;

  const votes = new Map<CoverageRequirementDomain, number>();
  for (const sectionId of item.sedSectionsHelpful) {
    const domain = SECTION_TO_DOMAIN[sectionId];
    if (domain) votes.set(domain, (votes.get(domain) ?? 0) + 1);
  }

  if (votes.size > 0) {
    let best: CoverageRequirementDomain = 'other';
    let bestCount = -1;
    for (const [domain, count] of votes) {
      if (count > bestCount) {
        best = domain;
        bestCount = count;
      }
    }
    const tied = [...votes.entries()].filter(([, c]) => c === bestCount).map(([d]) => d);
    if (tied.length === 1) return best;
    const firstSection = item.sedSectionsHelpful[0];
    const firstDomain = firstSection ? SECTION_TO_DOMAIN[firstSection] : undefined;
    if (firstDomain && tied.includes(firstDomain)) return firstDomain;
    return best;
  }

  const fromTags = domainFromTags(item.relevanceTagPattern);
  if (fromTags) return fromTags;

  return 'other';
}

/** Connectivity groups energy connections and network access in the UI.
 * Canonical domain classification remains available to source consumers. */
export function requirementDisplayDomain(item: ChecklistItem): CoverageRequirementDomain {
  const domain = classifyRequirementDomain(item);
  return domain === 'grid' ? 'connectivity' : domain;
}

export interface CoverageDomainGroup<T> {
  domain: CoverageRequirementDomain;
  label: string;
  items: T[];
}

/**
 * Group items by domain in stable display order. Empty domains are omitted.
 */
export function groupRequirementsByDomain<T extends { item: ChecklistItem }>(
  rows: readonly T[],
): CoverageDomainGroup<T>[] {
  const buckets = new Map<CoverageRequirementDomain, T[]>();
  for (const row of rows) {
    const domain = requirementDisplayDomain(row.item);
    const list = buckets.get(domain) ?? [];
    list.push(row);
    buckets.set(domain, list);
  }
  return COVERAGE_DOMAIN_ORDER
    .filter(domain => (buckets.get(domain)?.length ?? 0) > 0)
    .map(domain => ({
      domain,
      label: COVERAGE_DOMAIN_LABELS[domain],
      items: buckets.get(domain)!,
    }));
}
